import { z } from 'zod';

/**
 * Request contracts. Shared so the client and the server can never disagree
 * about a field name or a range.
 */

const localDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Fecha con formato YYYY-MM-DD');
const clockTime = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Hora con formato HH:MM');
const isoDateTime = z.string().datetime();
const rating = z.number().int().min(1).max(5);
const clientId = z.string().min(8).max(64);

export const tobaccoSchema = z.object({
  // 'iqos' (tabaco calentado) se cobra como la cajetilla: precio por paquete de
  // sticks entre sticks por paquete. No usa gramos.
  tobaccoType: z.enum(['pack', 'rolling', 'iqos', 'vape', 'mixed']),
  cigarettesPerDay: z.number().int().min(1).max(200),
  cigarettesPerPack: z.number().int().min(1).max(100).optional(),
  pricePerPackCents: z.number().int().min(1).max(100_000).optional(),
  pouchGrams: z.number().int().min(1).max(1000).nullable().optional(),
  gramsPerCigarette: z.number().min(0.1).max(5).nullable().optional(),
  pricePerPouchCents: z.number().int().min(1).max(100_000).nullable().optional(),
  // Papel + filtros por bolsa (solo liar). Suma al coste de la bolsa.
  accessoriesPerPouchCents: z.number().int().min(0).max(100_000).nullable().optional(),
  weeklySpendCents: z.number().int().min(0).max(1_000_000).nullable().optional(),
});

/** Home: powers context-aware missions (pet to pet, plants to water). */
export const householdSchema = z.object({
  hasPet: z.boolean().optional(),
  petType: z.enum(['dog', 'cat', 'other']).nullable().optional(),
  petName: z.string().max(40).nullable().optional(),
  hasPlants: z.boolean().optional(),
  hasPartner: z.boolean().optional(),
  hasChildren: z.boolean().optional(),
});

export const onboardSchema = z
  .object({
    mode: z.enum(['quitting', 'maintaining', 'smoking']),
    method: z.enum(['cold_turkey', 'cytisine']).nullable().optional(),
    /** Which treatment day the user is on today (1 = starting today). */
    treatmentDay: z.number().int().min(1).max(25).optional(),
    /**
     * Brand of cytisine the user is taking. Recigarum and Todacitan share the
     * same 25-day / 100-pill schedule; "otro" lets them type a name so the app
     * shows theirs instead of a default (docs/OPEN-QUESTIONS.md).
     */
    brand: z.string().min(1).max(60).optional(),
    firstDoseTime: clockTime.default('08:00'),
    /** Explicit Día D. May be in the past (maintaining) or future (reduction). */
    quitDate: isoDateTime.nullable().optional(),
    timezone: z.string().min(1).max(64).default('Europe/Madrid'),
    language: z.enum(['es', 'ca', 'ca-valencia', 'en', 'hu', 'eu', 'it', 'tr']).default('es'),
    currency: z.string().length(3).default('EUR'),
    tobacco: tobaccoSchema,
    household: householdSchema.optional(),
    /** Explicit consent to processing health data (RGPD art. 9). */
    consent: z.boolean().optional(),
  })
  .refine((data) => data.mode !== 'quitting' || data.method != null, {
    message: 'Si estás dejándolo hay que indicar el método',
    path: ['method'],
  })
  .refine((data) => data.method !== 'cytisine' || data.treatmentDay != null, {
    message: 'Con pastillas hay que indicar por qué día vas',
    path: ['treatmentDay'],
  });

export type OnboardInput = z.infer<typeof onboardSchema>;

export const settingsPatchSchema = z
  .object({
    mode: z.enum(['quitting', 'maintaining', 'smoking']),
    method: z.enum(['cold_turkey', 'cytisine']).nullable(),
    quitDate: isoDateTime.nullable(),
    reductionTargetPerDay: z.number().int().min(0).max(200).nullable(),
    language: z.enum(['es', 'ca', 'ca-valencia', 'en', 'hu', 'eu', 'it', 'tr']),
    timezone: z.string().min(1).max(64),
    currency: z.string().length(3),
    theme: z.enum(['system', 'light', 'dark']),
    reducedMotion: z.boolean(),
    notificationsEnabled: z.boolean(),
    notifyDoses: z.boolean(),
    notifyCheckin: z.boolean(),
    notifyMilestones: z.boolean(),
    notifyEncouragement: z.boolean(),
    quietHoursStart: clockTime,
    quietHoursEnd: clockTime,
    checkinHour: clockTime,
  })
  .merge(tobaccoSchema)
  .merge(householdSchema)
  .partial();

export const createPlanSchema = z.object({
  startDate: localDate,
  firstDoseTime: clockTime.default('08:00'),
  scheduleVersion: z.string().default('cytisine-25d-v1'),
  brand: z.string().trim().max(60).optional(),
});

export const doseActionSchema = z.object({
  takenAt: isoDateTime.optional(),
  notes: z.string().max(500).optional(),
});

export const doseRescheduleSchema = z.object({
  scheduledAt: isoDateTime,
});

export const doseSnoozeSchema = z.object({
  minutes: z.number().int().min(1).max(240).default(15),
});

export const smokeEventSchema = z.object({
  clientId,
  type: z.enum(['cigarette', 'slip', 'relapse']),
  occurredAt: isoDateTime,
  quantity: z.number().int().min(1).max(100).default(1),
  triggerId: z.number().int().nullable().optional(),
  mood: rating.nullable().optional(),
  stress: rating.nullable().optional(),
  intensity: rating.nullable().optional(),
  location: z.string().max(120).nullable().optional(),
  notes: z.string().max(1000).nullable().optional(),
});

/** What `PushSubscription.toJSON()` gives us in the browser. */
export const pushSubscribeSchema = z.object({
  endpoint: z.string().url().max(1000),
  keys: z.object({
    p256dh: z.string().min(1).max(200),
    auth: z.string().min(1).max(200),
  }),
});

export const pushOpenedSchema = z.object({
  type: z.string().min(1).max(40),
  refKey: z.string().min(1).max(120),
});

/**
 * Daily check-in. Everything optional: a check-in with only a mood is a valid
 * check-in, and demanding more would kill the 60-second promise.
 */
export const checkinSchema = z.object({
  clientId,
  mood: rating.nullable().optional(),
  energy: rating.nullable().optional(),
  stress: rating.nullable().optional(),
  sleepHours: z.number().min(0).max(24).nullable().optional(),
  exerciseMinutes: z.number().int().min(0).max(1440).nullable().optional(),
  waterMl: z.number().int().min(0).max(20_000).nullable().optional(),
  notes: z.string().max(2000).nullable().optional(),
});

export const journalEntrySchema = z.object({
  clientId,
  body: z.string().min(1).max(10_000),
  mood: rating.nullable().optional(),
  localDate: localDate.optional(),
});

export const journalPatchSchema = z.object({
  body: z.string().min(1).max(10_000).optional(),
  mood: rating.nullable().optional(),
});

/** Same payload as resolving by id, plus the clientId that identifies it. */
export const resolveByClientSchema = z.object({ clientId }).and(
  z.object({
    outcome: z.enum(['resisted', 'partial', 'smoked', 'abandoned']),
    stillWants: z.enum(['no', 'a_little', 'yes']).nullable().optional(),
    missionCompleted: z.boolean().optional(),
    notes: z.string().max(1000).nullable().optional(),
  }),
);

export const createGoalSchema = z.object({
  title: z.string().min(1).max(80),
  icon: z.string().min(1).max(8).default('🎯'),
  targetCents: z.number().int().min(100).max(100_000_000),
  /** Optional idempotency key so a goal created offline replays without duplicating. */
  clientId: clientId.optional(),
});

export const seenAchievementsSchema = z.object({
  ids: z.array(z.number().int()).max(100),
});

export const startCravingSchema = z.object({
  clientId,
  startedAt: isoDateTime.optional(),
  source: z.enum(['button', 'emergency']).default('button'),
  intensity: rating.nullable().optional(),
  triggerId: z.number().int().nullable().optional(),
});

export const updateCravingSchema = z.object({
  phaseReached: z.number().int().min(1).max(3).optional(),
  missionId: z.number().int().nullable().optional(),
  missionCompleted: z.boolean().optional(),
  intensity: rating.nullable().optional(),
  triggerId: z.number().int().nullable().optional(),
  notes: z.string().max(1000).nullable().optional(),
});

export const resolveCravingSchema = z.object({
  outcome: z.enum(['resisted', 'partial', 'smoked', 'abandoned']),
  stillWants: z.enum(['no', 'a_little', 'yes']).nullable().optional(),
  missionCompleted: z.boolean().optional(),
  /** Present when the craving ended in a cigarette; logs it in the same call. */
  smokeEvent: smokeEventSchema.omit({ type: true }).partial({ occurredAt: true }).optional(),
  notes: z.string().max(1000).nullable().optional(),
});

/** Registrar una situación condicionada superada (issue #63). */
export const situationWinSchema = z.object({
  clientId: z.string().min(1).max(64),
  triggerId: z.number().int().positive(),
  /** false = ni siquiera apeteció, que es la señal más fuerte de todas. */
  hadUrge: z.boolean().default(true),
  occurredAt: isoDateTime.optional(),
  notes: z.string().max(500).nullable().optional(),
});

export type SituationWinInput = z.infer<typeof situationWinSchema>;
